import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Minus, Warehouse, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, eur, errMsg } from "@/lib/api";
import { BrandBadge } from "@/components/shop/Badges";
import ProductForm from "@/pages/admin/ProductForm";
import { LowStockAlert, LOW_STOCK } from "@/pages/admin/LowStockAlert";

const rank = (p) => (p.stocked ? p.stock_rank : -1);
const SECTIONS = [
  { key: "stock", title: "Sandėlio modeliai", hint: "Laikomi sandėlyje — tempkite eilutes, kad pakeistumėte tvarką", test: (p) => rank(p) >= 0 },
  { key: "variants", title: "Proboss variantai pagal traktoriaus markę", hint: "Užsakomi", test: (p) => p.brand === "Proboss" && !!p.fits },
  { key: "order", title: "Užsakomi modeliai", hint: "Sandėlyje nelaikomi", test: () => true },
];
const groupProducts = (list) => {
  const used = new Set();
  return SECTIONS.map((s) => {
    const items = list.filter((p) => !used.has(p.id) && s.test(p));
    items.forEach((p) => used.add(p.id));
    if (s.key === "stock") items.sort((a, b) => rank(a) - rank(b));
    return { ...s, items };
  }).filter((s) => s.items.length);
};

const StockCell = ({ p, onSet }) => {
  const [v, setV] = useState(p.stock);
  useEffect(() => setV(p.stock), [p.stock]);
  const btn = "w-8 h-8 grid place-items-center border border-[#3B4157] hover:border-orange-500 hover:text-orange-500";
  return (
    <div className="flex items-center gap-1 justify-end">
      <button data-testid={`stock-minus-${p.id}`} className={btn} onClick={() => onSet(p, Math.max(0, p.stock - 1))}><Minus className="w-3 h-3" /></button>
      <input data-testid={`stock-input-${p.id}`} value={v} onChange={(e) => setV(e.target.value.replace(/\D/g, ""))}
        onBlur={() => Number(v) !== p.stock && onSet(p, Number(v || 0))} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        className={`w-14 h-8 text-center bg-[#1F2230] border border-[#3B4157] font-mono-x outline-none focus:border-orange-500 ${p.stock === 0 ? "text-red-400" : p.stock <= LOW_STOCK ? "text-amber-400" : ""}`} />
      <button data-testid={`stock-plus-${p.id}`} className={btn} onClick={() => onSet(p, p.stock + 1)}><Plus className="w-3 h-3" /></button>
    </div>
  );
};

export default function AdminProducts() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const setStock = async (p, stock) => {
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/stock`, { stock });
      setList((l) => l.map((x) => (x.id === p.id ? data : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [drag, setDrag] = useState(null);
  const dropOn = async (target) => {
    if (!drag || drag === target.id) return setDrag(null);
    const stocked = list.filter((p) => p.stocked).sort((a, b) => a.stock_rank - b.stock_rank);
    const ids = stocked.map((p) => p.id).filter((id) => id !== drag);
    ids.splice(ids.indexOf(target.id) + (stocked.findIndex((p) => p.id === drag) < stocked.findIndex((p) => p.id === target.id) ? 1 : 0), 0, drag);
    setDrag(null);
    setList((l) => l.map((p) => (p.stocked ? { ...p, stock_rank: ids.indexOf(p.id) + 1 } : p)));
    try { await api.post("/admin/stock-order", { ids }); toast.success("Eilės tvarka išsaugota"); } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const toggleHidden = async (p, hidden) => {
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/hidden`, { hidden });
      setList((l) => l.map((x) => (x.id === p.id ? data : x)));
      toast.success(hidden ? "Sėdynė paslėpta parduotuvėje" : "Sėdynė vėl rodoma parduotuvėje");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStocked = async (p) => {
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/stocked`, { stocked: !p.stocked });
      setList((l) => l.map((x) => (x.id === p.id ? data : x)));
      toast.success(data.stocked ? "Pažymėta: laikome sandėlyje" : "Perkelta į užsakomus modelius");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Ištrinti „${p.name}“?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Ištrinta");
    load();
  };

  return (
    <div data-testid="admin-products" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-display font-black uppercase text-4xl tracking-tight">Sėdynės</h1>
        <button data-testid="admin-add-product-btn" onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 h-11 px-5 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400">
          <Plus className="w-4 h-4" /> Nauja sėdynė
        </button>
      </div>
      <LowStockAlert items={list.filter((p) => rank(p) >= 0 && p.stock <= LOW_STOCK).sort((a, b) => rank(a) - rank(b))} />
      <div className="border border-[#3B4157] bg-[#292D3C] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead><tr className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-[#3B4157]">
            <th className="p-3 pl-5">Sėdynė</th><th className="p-3">Gamintojas</th><th className="p-3 text-right">Kaina</th><th className="p-3 text-right">Peržiūros</th><th className="p-3 text-right">Likutis</th><th className="p-3 text-center">Nerodyti</th><th className="p-3 pr-5" />
          </tr></thead>
          <tbody>
            {groupProducts(list).map((sec) => [
              <tr key={sec.key} data-testid={`admin-section-${sec.key}`} className="bg-[#1F2230] border-b border-[#3B4157]">
                <td colSpan={7} className={`p-3 pl-5 ${sec.key === "stock" ? "border-l-2 border-orange-500" : "border-l-2 border-slate-600"}`}>
                  <span className="font-display font-bold uppercase tracking-widest text-sm">{sec.title}</span>
                  <span className="ml-3 text-xs text-slate-500">{sec.hint} · {sec.items.length}</span>
                </td>
              </tr>,
              ...sec.items.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`}
                draggable={sec.key === "stock"} onDragStart={() => setDrag(p.id)} onDragEnd={() => setDrag(null)}
                onDragOver={(e) => sec.key === "stock" && drag && e.preventDefault()} onDrop={() => dropOn(p)}
                className={`${sec.key === "stock" ? "cursor-grab active:cursor-grabbing" : ""} ${drag === p.id ? "opacity-40" : ""} border-b border-[#3B4157] last:border-0 hover:bg-[#30354A] ${sec.key === "stock" && p.stock <= LOW_STOCK ? "bg-amber-500/5" : ""} ${sec.key !== "stock" ? "opacity-80" : ""}`}>
                <td className="p-3 pl-5"><div className="flex items-center gap-3">{sec.key === "stock" && <GripVertical data-testid={`drag-handle-${p.id}`} className="w-4 h-4 text-slate-600 shrink-0 -ml-2" />}<img src={imgUrl(p.images?.[0])} alt="" className="w-10 h-10 object-contain p-0.5 img-stage" /><div><div>{p.name}</div><div className="text-xs text-slate-500 font-mono-x">{p.sku}{!p.active && <span className="text-amber-400"> · paslėpta</span>}</div></div></div></td>
                <td className="p-3"><BrandBadge brand={p.brand} /></td>
                <td className="p-3 text-right font-mono-x">{p.price_on_request ? <span className="text-orange-400">Klausti kainos</span> : <>{eur(p.price)} <span className="text-[10px] text-slate-500">be PVM</span>{p.discount_pct > 0 && <div data-testid={`admin-product-sale-${p.id}`} className="text-[11px] text-red-400">−{p.discount_pct}% → {eur(p.price * (1 - p.discount_pct / 100))}</div>}</>}{p.fits && <div className="text-[10px] text-slate-500">{p.fits}</div>}</td>
                <td className="p-3 text-right font-mono-x text-slate-400">{p.views}</td>
                <td className="p-3"><StockCell p={p} onSet={setStock} /></td>
                <td className="p-3 text-center"><input data-testid={`hide-checkbox-${p.id}`} type="checkbox" checked={!p.active} onChange={(e) => toggleHidden(p, e.target.checked)} title="Nerodyti sėdynės parduotuvėje" className="accent-orange-500 w-4 h-4 cursor-pointer" /></td>
                <td className="p-3 pr-5">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`stocked-toggle-${p.id}`} onClick={() => toggleStocked(p)} title={p.stocked ? "Laikome sandėlyje – spauskite, kad perkeltumėte į užsakomus" : "Pažymėti, kad laikome sandėlyje"}
                      className={`w-8 h-8 grid place-items-center transition-colors ${p.stocked ? "text-orange-500 hover:text-slate-400" : "text-slate-600 hover:text-orange-500"}`}><Warehouse className="w-4 h-4" /></button>
                    <button data-testid={`admin-edit-${p.id}`} onClick={() => setEditing(p)} className="w-8 h-8 grid place-items-center text-slate-400 hover:text-orange-500"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p)} className="w-8 h-8 grid place-items-center text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))])}
          </tbody>
        </table>
      </div>
      {editing && <ProductForm product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
